/**
 * App entry point (parts 11, 15, 16b, 18, 19a, 20, 22, 24a, 40a, 36a): boots the app into the page with the browser's own
 * objects, the layout worker, the bundle worker, the EPUB worker, the DOCX worker, the image worker, the import worker, the find worker and the
 * service worker; from part 36a the account, when the build names a relay (its KDF worker and `fetch`); from part 36c
 * the clipboard and the print dialog the backup codes use; from part 41b the KDF worker for the backup lock, always.
 */
import { boot } from './boot.ts';
import { clipboardCopier, printer, shareSheetOf } from './shell/page-tools.ts';
import { EN } from './i18n/en.ts';
import { layoutWorker, presentBitmap, watchSize } from './preview/platform.ts';
import workerUrl from './preview/layout.worker.ts?worker&url';
import bundleUrl from './bundle/bundle.worker.ts?worker&url';
import { bundleWorker } from './bundle/platform.ts';
import backupUrl from './backup/backup.worker.ts?worker&url';
import { backupWorker } from './backup/platform.ts';
import { folderPicker, fileSharer } from './backup/browser.boundary.ts';
import epubUrl from './epub/epub.worker.ts?worker&url';
import { epubWorker } from './epub/platform.ts';
import docxUrl from './docx/docx.worker.ts?worker&url';
import { docxWorker } from './docx/platform.ts';
import imagesUrl from './images/images.worker.ts?worker&url';
import importUrl from './import/import.worker.ts?worker&url';
import { importWorker } from './import/platform.ts';
import findUrl from './find/find.worker.ts?worker&url';
import { findWorker } from './find/platform.ts';
import { sha256 } from './images/digest.ts';
import { imageWorker } from './images/platform.ts';
import { workerUrlFactory } from './preview/worker-url.boundary.ts';
import { isContainer } from './pwa/controller.ts';
import { outsideHomeScreen } from './pwa/home-screen.ts';
import { SERVICE_WORKER_URL } from 'virtual:service-worker';
import kdfUrl from './kdf/kdf.worker.ts?worker&url';
import sealUrl from './share/seal.worker.ts?worker&url';
import { sealWorker } from './share/seal.ts';
import { kdfWorker } from './kdf/platform.ts';
import { readAccountConfig } from './account/config.ts';
import './style.css';

const root = document.getElementById('app');
if (root !== null) {
  // One Trusted Types policy for every worker the app starts: the CSP names it once.
  const trusted = workerUrlFactory(Reflect.get(globalThis, 'trustedTypes'), location.origin);
  const script = SERVICE_WORKER_URL;
  // U1: read by shape: a browser may name the container and hold nothing there.
  const container: unknown = Reflect.get(navigator, 'serviceWorker');
  // Part 36a, AC1: accounts only when the build names the relay, its trial key and the site (the user's choice, 2026-10-23).
  const config = readAccountConfig(import.meta.env);
  boot({
    window,
    root,
    indexedDB,
    locks: 'locks' in navigator ? navigator.locks : undefined,
    storage: 'storage' in navigator ? navigator.storage : undefined,
    channel: (name) => new BroadcastChannel(name),
    random: (bytes) => crypto.getRandomValues(bytes),
    locale: 'en',
    catalog: EN,
    layoutWorker: layoutWorker({ Worker, trusted, url: workerUrl }),
    bundleWorker: bundleWorker({ Worker, trusted, url: bundleUrl }),
    backupWorker: backupWorker({ Worker, trusted, url: backupUrl }),
    // Part 41b: the backup lock's KDF worker, with accounts off too.
    kdfWorker: kdfWorker({ Worker, trusted, url: kdfUrl }),
    pickFolder: folderPicker(window),
    shareFile: fileSharer(navigator, matchMedia('(pointer: coarse)').matches),
    epubWorker: epubWorker({ Worker, trusted, url: epubUrl }),
    docxWorker: docxWorker({ Worker, trusted, url: docxUrl }),
    imageWorker: imageWorker({ Worker, trusted, url: imagesUrl }),
    importWorker: importWorker({ Worker, trusted, url: importUrl }),
    findWorker: findWorker({ Worker, trusted, url: findUrl }),
    digest: (bytes) => sha256(crypto, bytes),
    present: presentBitmap,
    watchSize: (element, changed) => watchSize(ResizeObserver, element, changed),
    objectUrl: (file, type) => URL.createObjectURL(file instanceof Blob ? file : new Blob([file], { type })),
    revokeUrl: (url) => {
      URL.revokeObjectURL(url);
    },
    serviceWorkers: isContainer(container) ? container : undefined,
    serviceWorkerUrl: script === null ? null : () => trusted(script),
    outsideHomeScreen: outsideHomeScreen(
      { userAgent: navigator.userAgent, maxTouchPoints: navigator.maxTouchPoints, standalone: Reflect.get(navigator, 'standalone') },
      matchMedia('(display-mode: standalone)').matches,
    ),
    choiceStorage: () => localStorage,
    unsavedStorage: () => localStorage,
    // Part 36c, FX4: the backup codes' Copy and Print.
    copy: clipboardCopier(navigator.clipboard),
    print: printer(window, 'codes'),
    shareSheet: shareSheetOf(navigator),
    ...(config === null
      ? {}
      : {
          account: {
            config,
            fetch: (url, init) => fetch(url, { ...init, credentials: 'omit', cache: 'no-store', referrerPolicy: 'no-referrer' }),
            kdfWorker: kdfWorker({ Worker, trusted, url: kdfUrl }),
            sealWorker: sealWorker({ Worker, trusted, url: sealUrl }),
            subtle: crypto.subtle,
            credentials: 'credentials' in navigator ? navigator.credentials : undefined,
          },
        }),
  });
}
